import { AppThunk } from '@/store'
import { createAction, request } from '@/store/actions'

import {
  ADD_SPACE_MEMBER_FROM_ROLE,
  FETCH_SPACE_MEMBERS_BY_NOT_ROLE,
  FETCH_SPACE_MEMBERS_BY_ROLE,
  REMOVE_SPACE_MEMBER_FROM_ROLE,
  RESET_SPACE_MEMBERS_ROLES_PAGINATION_STATE
} from './actionTypes'

/**
 * Fetch members from a space and role
 *
 * @param roleGuid the role guid
 * @param page which page number to start at
 * @param membersPerPage max number of members per page
 * @param sortBy field name to sort by
 * @param direction the sorting direction
 * @returns {function(*): *}
 */
export const fetchSpaceMembersInRole = (
  roleGuid: string,
  page: number,
  membersPerPage: number,
  sortBy: string,
  direction: string
): AppThunk => {
  return dispatch => {
    // A little bit of math for the start parameter since the backend starts at '0'
    return dispatch(
      request(
        `/organizations/:organizationId/spaces/:spaceId/users/roles/${roleGuid}`,
        FETCH_SPACE_MEMBERS_BY_ROLE,
        {
          method: 'POST',
          body: {
            startIndex: (page - 1) * membersPerPage + 1,
            count: membersPerPage,
            sortBy,
            sortOrder: direction
          }
        },
        { args: { roleGuid, sortBy, direction } }
      )
    )
  }
}

/**
 * Fetch members from a space and role
 *
 * @param roleGuid the role guid
 * @param query the name filter
 * @returns {function(*): *}
 */
export const fetchSpaceMembersNotInRole = (roleGuid: string, query: any): AppThunk => {
  return dispatch => {
    // A little bit of math for the start parameter since the backend starts at '0'
    return dispatch(
      request(
        `/organizations/:organizationId/spaces/:spaceId/users/roles/${roleGuid}`,
        FETCH_SPACE_MEMBERS_BY_NOT_ROLE,
        {
          method: 'POST',
          body: query
        }
      )
    )
  }
}

/**
 * Remove a user from a role and refresh the list of members to display
 *
 * @param userId the user guid
 * @param roleGuid the role guid
 * @returns {function(*): *}
 */
export const removeUserFromRole = (userId: string, roleGuid: string): AppThunk => {
  return dispatch => {
    return dispatch(
      request(
        `/organizations/:organizationId/spaces/:spaceId/users/${userId}/roles/${roleGuid}`,
        REMOVE_SPACE_MEMBER_FROM_ROLE,
        { method: 'DELETE' },
        { args: { organizationId: ':organizationId', spaceId: ':spaceId' } }
      )
    )
  }
}

/**
 * Add a user to a role and refresh the list of members to display
 *
 * @param userId the user guid
 * @param roleGuid the role guid
 * @returns {function(*): *}
 */
export const addUserToRole = (userId: string, roleGuid: string): AppThunk => {
  return dispatch => {
    return dispatch(
      request(
        `/organizations/:organizationId/spaces/:spaceId/users/${userId}/roles`,
        ADD_SPACE_MEMBER_FROM_ROLE,
        { method: 'POST', body: { roleId: roleGuid } },
        { args: { organizationId: ':organizationId', spaceId: ':spaceId' } }
      )
    )
  }
}

export const resetPaginationState = (): AppThunk => {
  return dispatch => dispatch(createAction(RESET_SPACE_MEMBERS_ROLES_PAGINATION_STATE))
}
